// @spec AST-STR-011
// @spec AST-STR-013
// @spec AST-STR-015
// @spec AST-STR-016
'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileText, Plus, ShieldCheck, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { RiskSecurityModuleContext } from '@/components/risk-security-module-context';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { fetchRegistryStats } from '@/features/controls/api/controls';
import { useControls } from '@/features/controls/api/use-controls';
import { BulkMappingDialog } from '@/features/controls/components/BulkMappingDialog';
import { ControlFilters as ControlFiltersComponent, hasActiveFilters } from '@/features/controls/components/ControlFilters';
import { ControlRegistryTable } from '@/features/controls/components/ControlRegistryTable';
import { CreateControlDialog } from '@/features/controls/components/CreateControlDialog';
import { FrameworkImportDialog } from '@/features/controls/components/FrameworkImportDialog';
import { useControlFiltersStore } from '@/features/controls/stores/control-filters-store';
import {
  CONTROL_FRAMEWORK_LABELS,
  type Control,
  type ControlFramework,
  type ControlSortField,
  type ControlSortOrder,
} from '@/features/controls/types';
import { useOrganizationId } from '@/hooks/use-organization-id';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

const REGISTRY_PAGE_SIZE = 20;
const REGISTRY_FETCH_PAGE_SIZE = 100;

function sortControls(
  controls: Control[],
  sortBy: ControlSortField,
  sortOrder: ControlSortOrder
): Control[] {
  const next = [...controls];

  next.sort((a, b) => {
    let left = '';
    let right = '';

    switch (sortBy) {
      case 'title':
        left = a.title;
        right = b.title;
        break;
      case 'framework':
        left = a.framework;
        right = b.framework;
        break;
      case 'category':
        left = a.category ?? '';
        right = b.category ?? '';
        break;
      case 'control_code':
      default:
        left = a.controlCode;
        right = b.controlCode;
        break;
    }

    const compared = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
    return sortOrder === 'desc' ? compared * -1 : compared;
  });

  return next;
}

export const Route = createFileRoute('/_authenticated/controls/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.controlsRegistry'),
  }),
  component: ControlsPage,
});

/**
 * Controls page - comprehensive controls registry.
 */
function ControlsPage() {
  const { t } = useTranslation('controls');
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const organizationId = useOrganizationId();

  // Create control dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Import framework dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Bulk mapping dialog state
  const [bulkMappingDialogOpen, setBulkMappingDialogOpen] = useState(false);
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([]);

  // Filter state from store
  const filters = useControlFiltersStore.use.filters();
  const page = useControlFiltersStore.use.page();
  const setFramework = useControlFiltersStore.use.setFramework();
  const setSearch = useControlFiltersStore.use.setSearch();
  const setIsStandard = useControlFiltersStore.use.setIsStandard();
  const setRegulation = useControlFiltersStore.use.setRegulation();
  const setSort = useControlFiltersStore.use.setSort();
  const setPage = useControlFiltersStore.use.setPage();
  const clearFilters = useControlFiltersStore.use.clearFilters();

  // Fetch controls with broad page size, then paginate in registry UI
  const {
    controls: fetchedControls,
    isLoading,
    error,
    refetch,
  } = useControls({
    filters,
    page: 1,
    pageSize: REGISTRY_FETCH_PAGE_SIZE,
  });

  const controlsForRegistry = useMemo(() => {
    return sortControls(fetchedControls, filters.sortBy, filters.sortOrder);
  }, [fetchedControls, filters.sortBy, filters.sortOrder]);

  const registryTotal = controlsForRegistry.length;
  const registryTotalPages = registryTotal > 0 ? Math.ceil(registryTotal / REGISTRY_PAGE_SIZE) : 0;

  useEffect(() => {
    if (registryTotalPages > 0 && page > registryTotalPages) {
      setPage(registryTotalPages);
      return;
    }

    if (registryTotalPages === 0 && page !== 1) {
      setPage(1);
    }
  }, [page, registryTotalPages, setPage]);

  const pagedControls = useMemo(() => {
    const start = Math.max(0, (page - 1) * REGISTRY_PAGE_SIZE);
    return controlsForRegistry.slice(start, start + REGISTRY_PAGE_SIZE);
  }, [controlsForRegistry, page]);

  const {
    data: registryStats,
    refetch: refetchRegistryStats,
  } = useQuery({
    queryKey: ['controls', 'registry-stats', organizationId],
    enabled: Boolean(organizationId) && !error,
    staleTime: 30_000,
    queryFn: () => fetchRegistryStats(getToken, organizationId!),
  });

  const mappedControlsCount = registryStats?.mappedControls ?? 0;
  const implementedControlsCount = registryStats?.implementedControls ?? 0;
  const trackedAssetsCount = registryStats?.linkedAssets ?? 0;

  // Handle sort change
  const handleSortChange = (sortBy: typeof filters.sortBy, sortOrder: typeof filters.sortOrder) => {
    setSort(sortBy, sortOrder);
  };

  const handleApplyRegulationFilter = (regulation: string) => {
    setFramework(null);
    setRegulation(regulation);
  };

  const handleFocusCustomControls = () => {
    setIsStandard(false);
  };

  const handleResetRegistryFilters = () => {
    clearFilters();
  };

  const handleFocusFramework = (framework: ControlFramework) => {
    setFramework([framework]);
    setRegulation(null);
  };

  const handleRefreshData = () => {
    refetch();
    void refetchRegistryStats();
  };

  const handleControlRowClick = (control: Control) => {
    void navigate({
      to: '/controls/$controlId',
      params: { controlId: control.id },
    });
  };

  // Handle selection change from table
  const handleSelectionChange = (ids: string[]) => {
    setSelectedControlIds(ids);
  };

  // Handle bulk mapping success
  const handleBulkMappingSuccess = () => {
    refetch();
    void refetchRegistryStats();
    setSelectedControlIds([]);
  };

  const hasFiltersActive = hasActiveFilters(filters);
  const hasSelection = selectedControlIds.length > 0;

  let activeFramework: ControlFramework | null = null;
  if (filters.framework?.length === 1) {
    const [singleFramework] = filters.framework;
    if (singleFramework) {
      activeFramework = singleFramework;
    }
  }

  const activeFrameworkLabel =
    activeFramework !== null ? CONTROL_FRAMEWORK_LABELS[activeFramework] : null;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-[1400px] space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('registryPage.title')}</h1>
            <p className="mt-1 text-muted-foreground">
              {isLoading ? (
                t('registryPage.loading')
              ) : (
                <>
                  {t('registryPage.controlsAvailable', { count: registryTotal })}
                  {activeFrameworkLabel ? t('registryPage.inFramework', { framework: activeFrameworkLabel }) : ''}
                </>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            {hasSelection && (
              <Button
                variant="outline"
                onClick={() => {
                  setBulkMappingDialogOpen(true);
                }}
              >
                <FileText className="mr-2 h-4 w-4" />
                {t('registryPage.manageMappings', { count: selectedControlIds.length })}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setImportDialogOpen(true);
              }}
            >
              <Upload className="mr-2 h-4 w-4" />
              {t('registryPage.importFramework')}
            </Button>
            <Button
              onClick={() => {
                setCreateDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('registryPage.addCustomControl')}
            </Button>
          </div>
        </header>

        <RiskSecurityModuleContext module="controls" />

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="mb-4 text-sm text-destructive">
              {error instanceof Error ? error.message : t('registryPage.failedToLoad')}
            </p>
            <Button variant="outline" onClick={handleRefreshData}>
              {t('registryPage.tryAgain')}
            </Button>
          </div>
        )}

        {!error && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="space-y-4 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <p className="text-base font-medium">
                    {t('registryPage.browseDescription')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('registryPage.coverageSummary', {
                      mapped: mappedControlsCount,
                      total: Math.max(registryTotal, 1),
                      implemented: implementedControlsCount,
                      assets: trackedAssetsCount,
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="bg-white/80">
                    <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                    {t('registryPage.implementationTracking')}
                  </Badge>
                  <Badge variant="outline" className="bg-white/80">
                    {t('registryPage.crossRegulationMapping')}
                  </Badge>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleFocusFramework('iso_27001_2022');
                  }}
                >
                  {t('registryPage.iso27001AnnexA')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleApplyRegulationFilter('NIS2');
                  }}
                >
                  {t('registryPage.nis2TechnicalRequirements')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleApplyRegulationFilter('EU AI Act');
                  }}
                >
                  {t('registryPage.euAiAct')}
                </Button>
                <Button variant="outline" size="sm" onClick={handleFocusCustomControls}>
                  {t('registryPage.customControls')}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleResetRegistryFilters}>
                  {t('registryPage.resetFilters')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        {!error && (
          <ControlFiltersComponent
            filters={filters}
            onFrameworkChange={setFramework}
            onSearchChange={setSearch}
            onIsStandardChange={setIsStandard}
            onRegulationChange={setRegulation}
            onSortChange={handleSortChange}
            onClearFilters={clearFilters}
          />
        )}

        {/* Controls registry table */}
        {!error && (
          <ControlRegistryTable
            controls={pagedControls}
            page={page}
            pageSize={REGISTRY_PAGE_SIZE}
            total={registryTotal}
            totalPages={registryTotalPages}
            onPageChange={setPage}
            onRowClick={handleControlRowClick}
            selectable
            selectedIds={selectedControlIds}
            onSelectionChange={handleSelectionChange}
          />
        )}

        {/* Empty state with filters */}
        {!error && !isLoading && controlsForRegistry.length === 0 && hasFiltersActive && (
          <div className="text-center">
            <Button variant="outline" onClick={clearFilters}>
              {t('registryPage.clearAllFilters')}
            </Button>
          </div>
        )}

        {/* Create Control Dialog */}
        <CreateControlDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSuccess={() => {
            refetch();
            void refetchRegistryStats();
          }}
        />

        {/* Bulk Mapping Dialog */}
        <BulkMappingDialog
          open={bulkMappingDialogOpen}
          onOpenChange={setBulkMappingDialogOpen}
          selectedControlIds={selectedControlIds}
          onSuccess={handleBulkMappingSuccess}
        />

        {/* Framework Import Dialog */}
        <FrameworkImportDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          onSuccess={() => {
            refetch();
            void refetchRegistryStats();
          }}
        />

      </div>
    </div>
  );
}
